import { Injectable, NgZone, inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class MobileVisualViewportService {
  private readonly ngZone = inject(NgZone);
  private activeConsumers = 0;
  private updateTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly updateHandler = (): void => this.scheduleUpdate();

  public activate(): void {
    this.activeConsumers++;

    if (this.activeConsumers > 1) {
      this.updateViewportVars();
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      window.visualViewport?.addEventListener('resize', this.updateHandler);
      window.visualViewport?.addEventListener('scroll', this.updateHandler);
      window.addEventListener('resize', this.updateHandler);
      window.addEventListener('orientationchange', this.updateHandler);
      window.addEventListener('focusin', this.updateHandler);
      window.addEventListener('focusout', this.updateHandler);
    });

    this.updateViewportVars();
  }

  public deactivate(): void {
    this.activeConsumers = Math.max(this.activeConsumers - 1, 0);

    if (this.activeConsumers > 0) {
      return;
    }

    window.visualViewport?.removeEventListener('resize', this.updateHandler);
    window.visualViewport?.removeEventListener('scroll', this.updateHandler);
    window.removeEventListener('resize', this.updateHandler);
    window.removeEventListener('orientationchange', this.updateHandler);
    window.removeEventListener('focusin', this.updateHandler);
    window.removeEventListener('focusout', this.updateHandler);

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
  }

  private scheduleUpdate(): void {
    this.updateViewportVars();

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      this.updateTimeout = null;
      this.updateViewportVars();
    }, 250);
  }

  private updateViewportVars(): void {
    const viewport = window.visualViewport;
    const viewportHeight = viewport?.height || window.innerHeight;
    const viewportOffsetTop = viewport?.offsetTop || 0;
    const layoutViewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const keyboardInsetBottom = Math.max(0, layoutViewportHeight - viewportHeight - viewportOffsetTop);
    const rootStyle = document.documentElement.style;

    rootStyle.setProperty('--app-visual-viewport-height', `${viewportHeight}px`);
    rootStyle.setProperty('--app-visual-viewport-offset-top', `${viewportOffsetTop}px`);
    rootStyle.setProperty('--app-keyboard-inset-bottom', `${keyboardInsetBottom}px`);
  }
}
